package one.healthium.fodmap.presentation.navigation.main

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import com.arkivanov.decompose.extensions.compose.stack.Children
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.composables.core.SheetDetent
import com.composables.core.SheetDetent.Companion.FullyExpanded
import com.composables.core.SheetDetent.Companion.Hidden
import kotlinx.coroutines.delay
import one.healthium.content.navigation.content.ContentContent
import one.healthium.content.navigation.media.VideoPlayerContent
import one.healthium.content.navigation.shopping_list.ShoppingListContent
import one.healthium.decompose.ModalT
import one.healthium.decompose.platformPredictiveBackAnimation
import one.healthium.fodmap.fodmap
import one.healthium.fodmap.presentation.navigation.home.HomeContent
import one.healthium.health_log.navigation.HealthLogContent
import one.healthium.log_food.ui.LogFoodContent
import one.healthium.log_food.ui.camera.FoodCameraContent
import one.healthium.log_food.ui.scanner.navigation.BarcodeScannerContent
import one.healthium.log_symptoms.navigation.LogSymptomsContent
import one.healthium.meal_plans.tutorial.navigation.PersonalMealPlanTutorialContent
import one.healthium.profile.navigation.log_water.LogWaterContent
import one.healthium.profile.navigation.log_weight.LogWeightContent
import one.healthium.profile.navigation.update_age.UpdateAgeContent
import one.healthium.profile.navigation.update_food.UpdateFoodContent
import one.healthium.profile.navigation.update_height.UpdateHeightContent
import one.healthium.profile.navigation.update_initial_weight.UpdateInitialWeightContent
import one.healthium.profile.navigation.update_name.UpdateNameContent
import one.healthium.profile.navigation.update_weight.UpdateWeightContent
import one.healthium.promo_banner.ui.PromoBannerBloc
import one.healthium.reminders.ui.RemindersBloc
import one.healthium.tutorial.navigation.TutorialContent
import one.healthium.ui.compose.components.AppModalBottomSheet
import one.healthium.ui.compose.components.rememberAppModalBottomSheetState
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.update_food_preferences.navigation.UpdateFoodPreferencesContent
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun MainContent(component: MainComponent) {
    koinViewModel<MainViewModel>()
    AppTheme(theme = fodmap()) {
        RemindersBloc {
            PromoBannerBloc {
                Box(Modifier.fillMaxSize().testTag("main_content")) {
                    Children(component = component)
                    ModalChildren(component = component)
                }
            }
        }
    }
}

@Composable
private fun Children(component: MainComponent) {
    Children(
        modifier = Modifier.fillMaxSize(),
        stack = component.stack,
        animation = platformPredictiveBackAnimation(
            backHandler = component.backHandler,
            onBack = component::onBackClicked,
        )
    ) {
        when (val child = it.instance) {
            is MainComponent.Child.Home -> HomeContent(child.component)
            is MainComponent.Child.Content -> ContentContent(child.component)
            is MainComponent.Child.Video -> VideoPlayerContent(child.component)
            is MainComponent.Child.ShoppingList -> ShoppingListContent(child.component)
            is MainComponent.Child.LogSymptoms -> LogSymptomsContent(child.component)
        }
    }
}

@Composable
private fun ModalChildren(component: MainComponent) {
    val state by component.stackModal.subscribeAsState()

    state.items.fastForEach { item ->
        ModalChild(
            wrapper = item.instance,
            onDismiss = {
                component.onDismissModal(item.configuration)
            },
        )
    }
}

@Composable
private fun ModalChild(
    wrapper: ModalT<MainComponent.SlotChild>,
    onDismiss: () -> Unit,
) {
    var dismissibleOverride by remember { mutableStateOf(false) }
    val child = wrapper.child
    fun canDismissSlot(detent: SheetDetent): Boolean {
        return if (detent == Hidden) {
            dismissibleOverride || (child !is MainComponent.SlotChild.Tutorial && child !is MainComponent.SlotChild.LogFood)
        } else {
            true
        }
    }

    val sheetState = rememberAppModalBottomSheetState(
        initialDetent = Hidden,
        velocityThreshold = { 300.dp },
        confirmDetentChange = ::canDismissSlot,
    )

    LaunchedEffect(wrapper) {
        if (wrapper.toDismiss) {
            dismissibleOverride = true
            sheetState.targetDetent = Hidden
        } else {
            dismissibleOverride = false
            delay(100)
            sheetState.targetDetent = FullyExpanded
        }
    }

    AppModalBottomSheet(
        state = sheetState,
        onDismissed = onDismiss,
        showConfetti = child is MainComponent.SlotChild.Tutorial,
    ) {
        when (child) {
            is MainComponent.SlotChild.LogFood -> LogFoodContent(child.component)
            is MainComponent.SlotChild.LogWater -> LogWaterContent(child.component)
            is MainComponent.SlotChild.LogWeight -> LogWeightContent(child.component)
            is MainComponent.SlotChild.UpdateWeight -> UpdateWeightContent(child.component)
            is MainComponent.SlotChild.UpdateFood -> UpdateFoodContent(child.component)
            is MainComponent.SlotChild.Tutorial -> TutorialContent(child.component)
            is MainComponent.SlotChild.UpdateName -> UpdateNameContent(child.component)
            is MainComponent.SlotChild.UpdateAge -> UpdateAgeContent(child.component)
            is MainComponent.SlotChild.UpdateHeight -> UpdateHeightContent(child.component)
            is MainComponent.SlotChild.UpdateInitialWeight -> UpdateInitialWeightContent(child.component)
            is MainComponent.SlotChild.BarcodeScanner -> BarcodeScannerContent(child.component)
            is MainComponent.SlotChild.LogSymptoms -> HealthLogContent(child.component)
            is MainComponent.SlotChild.UpdateFoodPreferences -> UpdateFoodPreferencesContent(child.component)
            is MainComponent.SlotChild.PersonalMealPlanTutorial -> PersonalMealPlanTutorialContent(child.component)
            is MainComponent.SlotChild.FoodCamera -> FoodCameraContent(child.component)
        }
    }
}
